/* Photo viewer (2026-09-29/30, user: every store needs one). Put data-rpb-zoom="<selector>" on a
   gallery; clicking a photo inside <selector> opens every photo there full screen. A photo is
   always shown whole, whatever its shape (tall 9:16 or wide 5:4), never cropped. Swipe, arrows,
   thumbnails or the keyboard (left/right) go to the next or previous photo. Zoom (up to 4x, around
   that point): pinch or double-tap on a phone; click, double-click or the mouse wheel on a
   computer. A zoomed photo is dragged to look around. The big X, Esc or the phone's back gesture
   closes it, leaving the gallery on the photo last viewed.
   Other galleries call window.rpbZoom.open([{src, alt, thumb}, ...], startIndex, onClose(index)). */
(() => {
  'use strict';
  const MAX = 4;
  // Big enough to zoom into, no bigger than the screen can use (phones decode 2000px slowly).
  const big = (src) => src.replace(/([?&]width=)\d+/, `$1${Math.min(innerWidth, innerHeight) < 700 ? 1400 : 2000}`);
  let dialog, track, thumbs, onClose, index = 0;
  let zoom = { s: 1, x: 0, y: 0 };
  let fit = null; // the current slide's box and the photo's fitted size, measured once per gesture
  let frame = 0;
  let heading = -1; // the photo an arrow or thumbnail is sliding to; scroll positions on the way are ignored

  function build() {
    dialog = document.createElement('dialog');
    dialog.className = 'rpb-zoom';
    dialog.setAttribute('aria-label', 'Photo viewer');
    dialog.innerHTML = `<div class="z-track"></div>
      <button type="button" class="z-close" data-z="close" aria-label="Close"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="m5 5 14 14M19 5 5 19"/></svg></button>
      <button type="button" class="z-nav" data-z="prev" aria-label="Previous photo"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="m15 5-7 7 7 7"/></svg></button>
      <button type="button" class="z-nav" data-z="next" aria-label="Next photo"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="m9 5 7 7-7 7"/></svg></button>
      <div class="z-thumbs"></div>`;
    document.body.append(dialog);
    track = dialog.querySelector('.z-track');
    thumbs = dialog.querySelector('.z-thumbs');

    dialog.addEventListener('click', (event) => {
      const button = event.target.closest('[data-z]');
      if (button) {
        const act = button.dataset.z;
        if (act === 'close') dialog.close();
        else go(index + (act === 'next' ? 1 : -1));
      }
      const thumb = event.target.closest('[data-z-shot]');
      if (thumb) go(Number(thumb.dataset.zShot));
    });
    dialog.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowRight') go(index + 1);
      else if (event.key === 'ArrowLeft') go(index - 1);
      else return;
      event.preventDefault();
    });
    dialog.addEventListener('close', () => {
      document.documentElement.classList.remove('rpb-zoom-open');
      if (onClose) onClose(index);
    });
    track.addEventListener('scroll', () => {
      if (zoom.s > 1) return;
      const i = Math.round(track.scrollLeft / track.clientWidth);
      if (heading >= 0) { if (i === heading) heading = -1; return; } // else the thumbnail border flickers back mid-slide
      if (i !== index && track.children[i]) { index = i; update(); }
    }, { passive: true });
    addEventListener('resize', () => {
      if (!dialog.open) return;
      setZoom(1, 0, 0);
      track.scrollLeft = index * track.clientWidth;
    });

    // Pointers: two fingers pinch; one finger/mouse drags a zoomed photo; a double tap (touch) or
    // a click (mouse) toggles zoom at that point.
    const points = new Map();
    let pinch = null, pan = null, lastTap = 0, downAt = null;
    track.addEventListener('pointerdown', (event) => {
      if (!event.target.closest('.z-slide')) return;
      points.set(event.pointerId, { x: event.clientX, y: event.clientY });
      downAt = { x: event.clientX, y: event.clientY };
      measure();
      if (points.size === 2) {
        const [a, b] = [...points.values()];
        pinch = { d: Math.hypot(a.x - b.x, a.y - b.y) || 1, s: zoom.s, x: zoom.x, y: zoom.y, mid: centre((a.x + b.x) / 2, (a.y + b.y) / 2) };
        pan = null;
      } else if (zoom.s > 1) {
        pan = { x: event.clientX - zoom.x, y: event.clientY - zoom.y };
        track.setPointerCapture(event.pointerId);
      }
    });
    track.addEventListener('pointermove', (event) => {
      if (!points.has(event.pointerId)) return;
      points.set(event.pointerId, { x: event.clientX, y: event.clientY });
      if (pinch && points.size === 2) {
        const [a, b] = [...points.values()];
        const s = clampScale(pinch.s * Math.hypot(a.x - b.x, a.y - b.y) / pinch.d);
        const k = s / pinch.s;
        setZoom(s, pinch.mid.x - (pinch.mid.x - pinch.x) * k, pinch.mid.y - (pinch.mid.y - pinch.y) * k);
      } else if (pan) {
        setZoom(zoom.s, event.clientX - pan.x, event.clientY - pan.y);
      }
    });
    const up = (event) => {
      if (!points.has(event.pointerId)) return;
      points.delete(event.pointerId);
      const pinched = Boolean(pinch);
      if (points.size < 2) pinch = null;
      if (pinched) downAt = null;
      if (!points.size) pan = null;
      if (!points.size && zoom.s > 1 && zoom.s < 1.15) setZoom(1, 0, 0); // a pinch let go near 1x settles at 1x
      const still = downAt && Math.hypot(event.clientX - downAt.x, event.clientY - downAt.y) < 10;
      if (event.type !== 'pointerup' || !still) return;
      const now = Date.now();
      const toggle = event.pointerType === 'mouse' || now - lastTap < 320;
      lastTap = toggle ? 0 : now;
      if (!toggle) return;
      if (zoom.s > 1) setZoom(1, 0, 0);
      else { const p = centre(event.clientX, event.clientY); setZoom(2.5, -p.x * 1.5, -p.y * 1.5); }
    };
    track.addEventListener('pointerup', up);
    track.addEventListener('pointercancel', up);
    // Wheel: up/down (or a trackpad pinch) zooms around the pointer; sideways scrolls to the
    // next photo, or moves around a zoomed one.
    track.addEventListener('wheel', (event) => {
      if (!event.target.closest('.z-slide')) return;
      const sideways = Math.abs(event.deltaX) > Math.abs(event.deltaY);
      if (sideways && !event.ctrlKey) {
        if (zoom.s > 1) { event.preventDefault(); setZoom(zoom.s, zoom.x - event.deltaX, zoom.y); }
        return;
      }
      event.preventDefault();
      measure();
      const s = clampScale(zoom.s * Math.exp(-event.deltaY * (event.ctrlKey ? 0.01 : 0.0025)));
      const p = centre(event.clientX, event.clientY), k = s / zoom.s;
      setZoom(s, p.x - (p.x - zoom.x) * k, p.y - (p.y - zoom.y) * k);
    }, { passive: false });
  }

  const clampScale = (s) => Math.min(MAX, Math.max(1, s));
  const current = () => track.children[index] && track.children[index].querySelector('img');
  // The slide's box and the size the photo is drawn at inside it (whole, centred).
  function measure() {
    const img = current();
    if (!img) return (fit = null);
    const box = img.parentElement.getBoundingClientRect();
    const pad = parseFloat(getComputedStyle(img.parentElement).paddingLeft) || 0;
    const bw = box.width - pad * 2, bh = box.height - pad * 2;
    const ratio = (img.naturalWidth || 1) / (img.naturalHeight || 1);
    const w = Math.min(bw, bh * ratio);
    fit = { left: box.left, top: box.top, width: box.width, height: box.height, w, h: w / ratio };
    return fit;
  }
  // A screen point relative to the current slide's centre.
  function centre(x, y) {
    const f = fit || measure();
    return { x: x - f.left - f.width / 2, y: y - f.top - f.height / 2 };
  }
  function setZoom(s, x, y) {
    const img = current();
    const f = fit || measure();
    if (!img || !f) return;
    const mx = Math.max(0, (f.w * s - f.width) / 2), my = Math.max(0, (f.h * s - f.height) / 2);
    const was = zoom.s > 1;
    zoom = { s, x: s > 1 ? Math.max(-mx, Math.min(mx, x)) : 0, y: s > 1 ? Math.max(-my, Math.min(my, y)) : 0 };
    if ((s > 1) !== was) dialog.classList.toggle('is-zoomed', s > 1);
    if (frame) return; // one paint per frame, however many pointer events arrive
    frame = requestAnimationFrame(() => {
      frame = 0;
      const now = current();
      if (now) now.style.transform = zoom.s > 1 ? `translate3d(${zoom.x}px, ${zoom.y}px, 0) scale(${zoom.s})` : '';
    });
  }
  function go(i) {
    const n = track.children.length;
    if (i < 0 || i >= n) return;
    setZoom(1, 0, 0);
    index = i;
    heading = i;
    clearTimeout(go.timer);
    go.timer = setTimeout(() => { heading = -1; }, 900); // in case the slide is interrupted
    track.scrollTo({ left: i * track.clientWidth, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
    update();
  }
  function update() {
    [...track.children].forEach((slide, i) => {
      const img = slide.querySelector('img');
      if (Math.abs(i - index) <= 1 && !img.src) img.src = img.dataset.src;
      if (i !== index) img.style.transform = '';
    });
    zoom = { s: 1, x: 0, y: 0 };
    fit = null;
    dialog.classList.remove('is-zoomed');
    const n = track.children.length;
    dialog.querySelector('[data-z="prev"]').disabled = index === 0;
    dialog.querySelector('[data-z="next"]').disabled = index === n - 1;
    thumbs.querySelectorAll('[data-z-shot]').forEach((t) => t.setAttribute('aria-current', String(Number(t.dataset.zShot) === index)));
    const on = thumbs.querySelector(`[data-z-shot="${index}"]`);
    if (on) thumbs.scrollTo({ left: on.offsetLeft - (thumbs.clientWidth - on.offsetWidth) / 2, behavior: 'smooth' });
  }

  function open(images, start, done) {
    if (!dialog) build();
    onClose = done;
    const esc = (v) => String(v || '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]);
    track.innerHTML = images.map((im) => `<div class="z-slide"><img data-src="${esc(big(im.src))}" alt="${esc(im.alt)}" draggable="false" decoding="async"></div>`).join('');
    thumbs.innerHTML = images.length > 1 ? images.map((im, i) => `<button type="button" data-z-shot="${i}" aria-label="Photo ${i + 1} of ${images.length}"><img src="${esc(im.thumb || im.src)}" alt=""></button>`).join('') : '';
    dialog.classList.toggle('is-single', images.length < 2);
    index = Math.min(images.length - 1, Math.max(0, start || 0));
    document.documentElement.classList.add('rpb-zoom-open');
    dialog.showModal();
    heading = -1;
    track.scrollLeft = index * track.clientWidth;
    update();
    dialog.querySelector('[data-z="close"]').focus({ preventScroll: true });
  }

  document.addEventListener('click', (event) => {
    const root = event.target.closest('[data-rpb-zoom]');
    if (!root || !(event.target instanceof HTMLImageElement)) return;
    const images = [...root.querySelectorAll(`${root.dataset.rpbZoom} img`)];
    const at = images.indexOf(event.target);
    if (at < 0) return;
    const slides = event.target.parentElement;
    open(images.map((im) => ({ src: im.currentSrc || im.src, alt: im.alt })), at,
      (i) => slides.scrollTo({ left: i * slides.clientWidth, behavior: 'instant' }));
  });
  window.rpbZoom = { open };
})();
